import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getTopVolumeRows, squareOffAnalysisSymbol, getAnalysisHistory, downloadAnalysisHistory, cleanupAnalysisData, getMomentumSuggestions, placeAnalysisTradeOrder, getAnalysisPositions, getAutopilotStatus, setAutopilotToggle, getManagePnL, setManagePnL, getTestMode, getTestModeScope, setTestMode, setAnalysisMaxQtyMultiplier, setAnalysisTradedValue } from '../services/api';
import './AnalysisPage.css';

const AUTOPILOT_BLOCK_MESSAGE = 'Autopilot is active — switch it off before placing manual trades.';

const TRADES_COLUMNS = [
  { key: 'symbol',       label: 'Symbol' },
  { key: 'type',         label: 'Type' },
  { key: 'qty',          label: 'Qty' },
  { key: 'avgPrice',     label: 'Avg. Price' },
  { key: 'amount',       label: 'Amount' },
  { key: 'currentPrice', label: 'Current Price' },
  { key: 'pnl',          label: 'PnL' },
  { key: 'pnlPct',       label: 'PnL%' },
  { key: null,           label: 'Actions' },
];

const COLUMNS = [
  { key: 'symbol',                  label: 'Symbol' },
  { key: 'price',                   label: 'Price' },
  { key: 'changeSincePrevScanPct',  label: 'Change in Price' },
  { key: 'changeFromPrevClosePct',  label: 'Change from Prev Close' },
  { key: 'changeFromHighPct',       label: 'Change from Day High' },
  { key: 'changeFromLowPct',        label: 'Change from Day Low' },
  { key: 'volume',                  label: 'Volume' },
  { key: 'buyQty',                  label: 'Buy Bids' },
  { key: 'sellQty',                 label: 'Sell Bids' },
];

const AUTO_DOWNLOAD_MINUTES = 15 * 60 + 30; // 3:30 PM IST

function nowIST() {
  return new Date(Date.now() + 5.5 * 60 * 60 * 1000);
}

function shouldAutoRefresh() {
  const istTime = nowIST();
  const day = istTime.getUTCDay();
  const timeInMinutes = istTime.getUTCHours() * 60 + istTime.getUTCMinutes();
  const isWeekday = day >= 1 && day <= 5;
  const isWithinTradingHours = timeInMinutes >= 550 && timeInMinutes <= 940; // 9:10 AM - 3:40 PM IST
  return isWeekday && isWithinTradingHours;
}

function istDateStr() {
  return nowIST().toISOString().substring(0, 10);
}

function formatISTTime(isoTs) {
  if (!isoTs) return '';
  const t = new Date(new Date(isoTs).getTime() + 5.5 * 60 * 60 * 1000);
  return t.toISOString().substring(11, 19);
}

// 3-of-5 vote: Change in Price, Buy/Sell bid dominance, Change from Prev
// Close each cast one BUY/SELL vote off their own sign. Change from Day
// High and Change from Day Low are structurally one-sided (price can never
// exceed today's own high, never go below today's own low), so instead of
// two near-constant votes they share ONE proximity comparison — whichever
// extreme price sits closer to — cast into BOTH of their slots, keeping
// the "5 slots, need 3" framing intact while making that comparison
// meaningful. Any exact tie defaults to BUY.
function computeSuggestedDirection(row) {
  const votes = [];
  votes.push(row.changeSincePrevScanPct < 0 ? 'SELL' : 'BUY');
  votes.push(row.sellQty > row.buyQty ? 'SELL' : 'BUY');
  votes.push(row.changeFromPrevClosePct < 0 ? 'SELL' : 'BUY');
  const distToHigh = Math.abs(row.changeFromHighPct);
  const distToLow  = Math.abs(row.changeFromLowPct);
  const proximityVote = distToHigh > distToLow ? 'SELL' : 'BUY';
  votes.push(proximityVote);
  votes.push(proximityVote);
  const buyCount = votes.filter(v => v === 'BUY').length;
  return buyCount >= 3 ? 'BUY' : 'SELL';
}

// The 4 "Change" columns are always DISPLAYED as absolute values (sign
// shown only via color) — sorting must match that, ranking by magnitude
// regardless of direction, not by signed value.
const ABS_SORT_KEYS = new Set(['changeSincePrevScanPct', 'changeFromPrevClosePct', 'changeFromHighPct', 'changeFromLowPct']);

function sortRows(rows, sortKey, sortDir) {
  const useAbs = ABS_SORT_KEYS.has(sortKey);
  const sorted = [...rows].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    // null (a symbol's first-ever tick today, only possible for
    // changeSincePrevScanPct) treated as 0 for sorting purposes only, so
    // it doesn't produce NaN.
    let an = av ?? 0, bn = bv ?? 0;
    if (useAbs) { an = Math.abs(an); bn = Math.abs(bn); }
    return sortDir === 'asc' ? an - bn : bn - an;
  });
  return sorted;
}

// Max Position Quantity cap check — mirrors the server's own proactive
// check in POST /trade-order (this is UX only; the server re-checks on
// every click regardless, since a stale render or a second tab could let a
// disabled-looking button through). Only the direction that would INCREASE
// exposure is ever capped — closing out is always allowed — and CNC is
// never capped.
function isCapBlocked(transactionType, currentQty, fixedQty, maxQty, product) {
  if (product === 'CNC' || !maxQty || !fixedQty) return false;
  if (transactionType === 'BUY') return currentQty >= 0 && currentQty + fixedQty > maxQty;
  return currentQty <= 0 && Math.abs(currentQty) + fixedQty > maxQty;
}

function saveBlobResponse(response, fallbackFilename) {
  const disposition = response.headers?.['content-disposition'] || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : fallbackFilename;
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function ChangeCell({ value, plain }) {
  if (value == null) return <td className="an-num">—</td>;
  const cls = plain ? '' : value > 0 ? 'an-pos' : value < 0 ? 'an-neg' : '';
  return <td className={`an-num ${cls}`}>{Math.abs(value).toFixed(2)}%</td>;
}

// Shared data cells (price through Sell Bids) for both a live row and a
// historical sub-row — same shape, same coloring rules. `plain` drops all
// red/green/dominance coloring, for the "Dropped from Top 50" grid.
function DataCells({ data, plain = false }) {
  return (
    <>
      <td className="an-num">{data.price.toFixed(2)}</td>
      <ChangeCell value={data.changeSincePrevScanPct} plain={plain} />
      <ChangeCell value={data.changeFromPrevClosePct} plain={plain} />
      <ChangeCell value={data.changeFromHighPct} plain={plain} />
      <ChangeCell value={data.changeFromLowPct} plain={plain} />
      <td className={`an-num ${!plain && data.volumeUp === true ? 'an-pos' : !plain && data.volumeUp === false ? 'an-neg' : ''}`}>
        {data.volume.toLocaleString('en-IN')}
      </td>
      <td className={`an-num ${!plain && data.buyQty > data.sellQty ? 'an-pos' : ''}`}>{data.buyQty.toLocaleString('en-IN')}</td>
      <td className={`an-num ${!plain && data.sellQty > data.buyQty ? 'an-pos' : ''}`}>{data.sellQty.toLocaleString('en-IN')}</td>
    </>
  );
}

// One of the two momentum-suggestion cards shown above the grid — the top
// 2 by score OVERALL, regardless of direction (see getMomentumSuggestions
// on the backend). `slot` only controls which side it sits on and which
// way it's justified; the color/label come from the suggestion's own
// direction, since either slot can hold either a BUY or a SELL.
function SuggestionCard({ slot, suggestion }) {
  const slotClass = slot === 'left' ? 'an-suggestion-left' : 'an-suggestion-right';
  if (!suggestion) {
    return (
      <div className={`an-suggestion ${slotClass} an-suggestion-empty`}>
        <span className="an-suggestion-empty-text">No strong momentum signal right now</span>
      </div>
    );
  }
  const isBuy = suggestion.direction === 'BUY';
  const confirmations = [];
  if (suggestion.directionalConsistency >= 0.6) confirmations.push('steady trend');
  if (suggestion.volumeConfirmation >= 0.6) confirmations.push('volume-backed');
  if (suggestion.depthConfirmation >= 0.6) confirmations.push('book-backed');
  return (
    <div className={`an-suggestion ${slotClass} ${isBuy ? 'an-suggestion-dir-buy' : 'an-suggestion-dir-sell'}`}>
      <span className="an-suggestion-label">{isBuy ? '▲ Buy Signal' : '▼ Sell Signal'}</span>
      <span className="an-suggestion-symbol">{suggestion.symbol}</span>
      <span className="an-suggestion-move">{suggestion.netMovePct > 0 ? '+' : ''}{suggestion.netMovePct.toFixed(2)}%</span>
      <span className="an-suggestion-price">@ {suggestion.price.toFixed(2)}</span>
      {confirmations.length > 0 && <span className="an-suggestion-tags">{confirmations.join(' • ')}</span>}
    </div>
  );
}

// Shows every live position — opened via this page or anywhere else (Kite
// directly, an algo, ...) — refreshed by the parent on the same 10s tick
// the main grid uses. Deliberately does NOT close on a backdrop click —
// only the header's close button does — since an accidental outside click
// shouldn't lose the user's place while reviewing live positions/PnL.
function TradesPopup({ positions, loading, tradingSymbol, onClose, onTrade }) {
  const [sortKey, setSortKey] = useState('symbol');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (key) => {
    if (!key) return;
    let dir = 'desc';
    if (sortKey === key) dir = sortDir === 'desc' ? 'asc' : 'desc';
    setSortKey(key);
    setSortDir(dir);
  };

  const sortedPositions = sortRows(positions, sortKey, sortDir);

  return (
    <div className="an-modal-backdrop">
      <div className="an-modal an-trades-modal" onClick={e => e.stopPropagation()}>
        <div className="an-trades-modal-header">
          <div className="an-modal-title">Trades</div>
          <button className="an-trades-modal-close" onClick={onClose} title="Close">×</button>
        </div>
        <div className="an-table-wrap">
          <table className="an-table">
            <thead>
              <tr>
                {TRADES_COLUMNS.map(col => (
                  <th
                    key={col.label}
                    onClick={() => handleSort(col.key)}
                    className={[col.key === 'symbol' ? 'an-symbol-header' : '', col.key ? 'an-sortable' : ''].filter(Boolean).join(' ')}
                  >
                    {col.label}
                    {col.key && sortKey === col.key && <span className="an-sort-arrow">{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedPositions.length === 0 && !loading ? (
                <tr><td colSpan={TRADES_COLUMNS.length} className="an-no-data">No open positions</td></tr>
              ) : sortedPositions.map(p => {
                const rowKey = `${p.symbol}-${p.product}`;
                const isBusy = tradingSymbol === rowKey;
                const buyCapped = isCapBlocked('BUY', p.qty, p.fixedQty, p.maxQty, p.product);
                const sellCapped = isCapBlocked('SELL', p.qty, p.fixedQty, p.maxQty, p.product);
                return (
                  <tr key={rowKey} className={p.atCap ? 'an-trades-row-capped' : ''}>
                    <td className="an-symbol">{p.symbol}</td>
                    <td className="an-num">{p.type}</td>
                    <td className="an-num">{p.qty}</td>
                    <td className="an-num">{p.avgPrice.toFixed(2)}</td>
                    <td className="an-num">{p.amount.toFixed(2)}</td>
                    <td className="an-num">{p.currentPrice.toFixed(2)}</td>
                    <td className={`an-num ${p.pnl > 0 ? 'an-pos' : p.pnl < 0 ? 'an-neg' : ''}`}>{Math.abs(p.pnl).toFixed(2)}</td>
                    <td className={`an-num ${p.pnlPct > 0 ? 'an-pos' : p.pnlPct < 0 ? 'an-neg' : ''}`}>{Math.abs(p.pnlPct).toFixed(2)}%</td>
                    <td>
                      <div className="an-actions">
                        <button
                          className={`an-btn-trade ${p.isLong ? 'an-btn-trade-buy' : ''}`}
                          onClick={() => onTrade(p.symbol, 'BUY', p.product)}
                          disabled={isBusy || buyCapped}
                          title={buyCapped ? 'Would exceed Max Position Quantity' : undefined}
                        >
                          {isBusy ? '…' : 'Buy'}
                        </button>
                        <button
                          className={`an-btn-trade ${!p.isLong ? 'an-btn-trade-sell' : ''}`}
                          onClick={() => onTrade(p.symbol, 'SELL', p.product)}
                          disabled={isBusy || sellCapped}
                          title={sellCapped ? 'Would exceed Max Position Quantity' : undefined}
                        >
                          {isBusy ? '…' : 'Sell'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AnalysisPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [sortKey, setSortKey] = useState('volume');
  const [sortDir, setSortDir] = useState('desc');
  const [amount, setAmount] = useState(() => localStorage.getItem('an-amount') || '50000');
  const [product, setProduct] = useState(() => localStorage.getItem('an-product') || 'MIS');
  const [maxQtyMultiplier, setMaxQtyMultiplier] = useState(() => parseInt(localStorage.getItem('an-max-qty-multiplier'), 10) || 10);
  const [autopilotEnabled, setAutopilotEnabledState] = useState(false);
  const [autopilotBusy, setAutopilotBusy] = useState(false);
  const [showAutopilotConfirm, setShowAutopilotConfirm] = useState(false);
  const [eligibleAlgos, setEligibleAlgos] = useState([]);
  const [selectedAutopilotType, setSelectedAutopilotType] = useState('');
  const [managePnLEnabled, setManagePnLEnabled] = useState(false);
  const [managePnLStatus, setManagePnLStatus] = useState(null); // { enabled, settings, floor } from the backend
  const [testMode, setTestModeState] = useState({ enabled: false, adjustment: null }); // from the backend
  const [testModeBusy, setTestModeBusy] = useState(false);
  const [testModePopup, setTestModePopup] = useState(null); // { enabling, choice } while the resize popup is open
  const [managePnLBusy, setManagePnLBusy] = useState(false);
  const [managePnLForm, setManagePnLForm] = useState(null); // non-null = config popup open
  const [placingOrder, setPlacingOrder] = useState(null);
  const [closingSymbol, setClosingSymbol] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [historyBySymbol, setHistoryBySymbol] = useState({});
  const [cleaningUp, setCleaningUp] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showTradesPopup, setShowTradesPopup] = useState(false);
  const [positions, setPositions] = useState([]);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [positionsError, setPositionsError] = useState('');
  const [tradingSymbol, setTradingSymbol] = useState(null);
  const sortRef = useRef({ sortKey, sortDir });
  sortRef.current = { sortKey, sortDir };
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  const amountRef = useRef(amount);
  amountRef.current = amount;
  const maxQtyMultiplierRef = useRef(maxQtyMultiplier);
  maxQtyMultiplierRef.current = maxQtyMultiplier;
  const showTradesPopupRef = useRef(showTradesPopup);
  showTradesPopupRef.current = showTradesPopup;
  const autopilotEnabledRef = useRef(autopilotEnabled);
  autopilotEnabledRef.current = autopilotEnabled;
  const pollRef = useRef(null);
  const autoDownloadedDateRef = useRef(localStorage.getItem('an-auto-downloaded-date') || '');

  const fetchHistoryFor = useCallback(async (symbol) => {
    try {
      const data = await getAnalysisHistory(symbol);
      setHistoryBySymbol(prev => ({ ...prev, [symbol]: data.history || [] }));
    } catch (err) {
      console.error('Failed to load history for', symbol, err);
    }
  }, []);

  const maybeAutoDownload = useCallback(async () => {
    const istTime = nowIST();
    const timeInMinutes = istTime.getUTCHours() * 60 + istTime.getUTCMinutes();
    const today = istDateStr();
    if (timeInMinutes < AUTO_DOWNLOAD_MINUTES) return;
    if (autoDownloadedDateRef.current === today) return;
    try {
      const response = await downloadAnalysisHistory();
      saveBlobResponse(response, `analysis-history-${today}.csv`);
      autoDownloadedDateRef.current = today;
      localStorage.setItem('an-auto-downloaded-date', today);
    } catch (err) {
      console.error('Auto-download failed:', err);
    }
  }, []);

  const fetchSuggestions = useCallback(async () => {
    try {
      const data = await getMomentumSuggestions();
      setSuggestions(data.top || []);
    } catch (err) {
      console.error('Failed to load suggestions', err);
    }
  }, []);

  const fetchPositions = useCallback(async () => {
    setPositionsLoading(true);
    try {
      const data = await getAnalysisPositions(amountRef.current, maxQtyMultiplierRef.current);
      setPositions(data.rows || []);
      setPositionsError('');
    } catch (err) {
      setPositionsError(err.response?.data?.error || err.message || 'Failed to load positions');
    } finally {
      setPositionsLoading(false);
    }
  }, []);

  const fetchRows = useCallback(async () => {
    try {
      const data = await getTopVolumeRows(amountRef.current, maxQtyMultiplierRef.current);
      const { sortKey: sk, sortDir: sd } = sortRef.current;
      setRows(sortRows(data.rows || [], sk, sd));
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
    for (const symbol of expandedRef.current) fetchHistoryFor(symbol);
    // Same tick refreshes Manage My P/L's state (no Kite call) — keeps the
    // switch and the Min Profit message current as the floor ratchets.
    getManagePnL()
      .then(status => { setManagePnLStatus(status); setManagePnLEnabled(!!status.enabled); })
      .catch(() => {});
    getTestMode().then(setTestModeState).catch(() => {});
    fetchSuggestions();
    maybeAutoDownload();
    // Trades popup refreshes on this exact same tick, only while it's open.
    if (showTradesPopupRef.current) fetchPositions();
  }, [fetchHistoryFor, fetchSuggestions, maybeAutoDownload, fetchPositions]);

  useEffect(() => {
    fetchRows();
    pollRef.current = setInterval(() => {
      if (shouldAutoRefresh()) fetchRows();
    }, 10000);
    return () => clearInterval(pollRef.current);
  }, [fetchRows]);

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(''), 5000);
    return () => clearTimeout(t);
  }, [message]);

  useEffect(() => { localStorage.setItem('an-amount', amount); }, [amount]);
  // Pushed to the backend on load and on every edit — the server-side
  // position-cap sweep runs with no browser involved, and must know when
  // Traded Value is 0 (the page then places no orders of any kind).
  useEffect(() => {
    setAnalysisTradedValue(parseFloat(amount) || 0).catch(err => console.error('Failed to sync Traded Value', err));
  }, [amount]);
  useEffect(() => { localStorage.setItem('an-product', product); }, [product]);
  useEffect(() => { localStorage.setItem('an-max-qty-multiplier', maxQtyMultiplier); }, [maxQtyMultiplier]);

  // The backend's Max Position Quantity multiplier is a single value shared
  // by every viewer AND the independent reconciliation sweep that runs
  // every ~10s regardless of any open browser tab — so it's only ever
  // changed here, explicitly, never implied by a background poll (a stale
  // request or a second tab could otherwise silently reset it for
  // everyone). Pushed once on mount too, so a value restored from
  // localStorage on a fresh page load actually takes effect server-side
  // right away instead of waiting for the user to touch the dropdown again.
  useEffect(() => {
    setAnalysisMaxQtyMultiplier(maxQtyMultiplier).catch(err => console.error('Failed to sync Max Position Quantity', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMaxQtyMultiplierChange = (value) => {
    setMaxQtyMultiplier(value);
    setAnalysisMaxQtyMultiplier(value).catch(err => console.error('Failed to sync Max Position Quantity', err));
  };

  // The backend is the source of truth for Autopilot — whichever algo is
  // running keeps going via the existing 10s strategy-execution scheduler
  // independent of this page, so a fresh page load (or a refresh mid-
  // session) must reflect whatever it's actually doing, not just whatever
  // this component's own state happens to default to.
  useEffect(() => {
    getAutopilotStatus()
      .then(status => {
        setAutopilotEnabledState(!!status.enabled);
        setEligibleAlgos(status.eligibleAlgos || []);
        if (status.activeType) setSelectedAutopilotType(status.activeType);
      })
      .catch(err => console.error('Failed to load Autopilot status', err));
    // Manage My P/L also runs entirely on the backend (every 10s tick).
    getManagePnL()
      .then(status => { setManagePnLStatus(status); setManagePnLEnabled(!!status.enabled); })
      .catch(err => console.error('Failed to load Manage My P/L status', err));
    getTestMode().then(setTestModeState).catch(err => console.error('Failed to load Test Mode status', err));
  }, []);

  const handleTrade = async (row, transactionType) => {
    if (autopilotEnabled) {
      setError(AUTOPILOT_BLOCK_MESSAGE);
      return;
    }
    setError('');
    setPlacingOrder({ symbol: row.symbol, type: transactionType });
    try {
      const result = await placeAnalysisTradeOrder({
        symbol: row.symbol,
        transactionType,
        product,
        amount, // fallback only — used if this symbol has no cached qty yet
        maxQtyMultiplier,
      });
      setMessage(`${transactionType} ${result.quantity}×${row.symbol} ${result.outcome === 'PLACED' ? 'placed' : result.outcome.toLowerCase()}`);
      fetchRows();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Order failed');
    } finally {
      setPlacingOrder(null);
    }
  };

  // positionProduct is THIS position's own product (CNC/MIS), not the
  // config bar's selector — a CNC holding must be bought/sold as CNC, an
  // intraday position as MIS, regardless of what's currently selected up
  // top (that selector only applies to opening a brand-new position from
  // the main grid, where there's no existing position to match).
  const handlePopupTrade = async (symbol, transactionType, positionProduct) => {
    if (autopilotEnabled) {
      setPositionsError(AUTOPILOT_BLOCK_MESSAGE);
      return;
    }
    setPositionsError('');
    // Composite key — a symbol can have both a CNC and an intraday row now,
    // so busy state must be scoped per-row, not per-symbol alone.
    setTradingSymbol(`${symbol}-${positionProduct}`);
    try {
      const result = await placeAnalysisTradeOrder({ symbol, transactionType, product: positionProduct, amount, maxQtyMultiplier });
      setMessage(`${transactionType} ${result.quantity}×${symbol} ${result.outcome === 'PLACED' ? 'placed' : result.outcome.toLowerCase()}`);
      fetchPositions();
    } catch (err) {
      setPositionsError(err.response?.data?.error || err.message || 'Order failed');
    } finally {
      setTradingSymbol(null);
    }
  };

  // Turning on: snapshot {amount, product, maxQtyMultiplier} to restore
  // later, then force the autopilot-mandated 5000/MIS/40x, and activate
  // whichever algorithm was selected in the confirm popup. Turning off:
  // restore exactly what was there before, and deactivate it. Either way,
  // the backend call is what actually (de)activates the algorithm server-
  // side via the same `active` flag the Algos page's own toggle uses — this
  // is just keeping the UI in sync and forcing/restoring the config fields.
  // The algo launched here has its own entirely separate Investment
  // Amount / Max Position Quantity (configured on the Algos page) and
  // always trades MIS — this toggle never touches the Traded
  // Value/Product Type/Max Position Quantity fields below, in either
  // direction. They're the user's own manual-trading settings, untouched
  // by whatever Autopilot is doing.
  const handleAutopilotToggle = async (checked) => {
    setAutopilotBusy(true);
    try {
      const status = await setAutopilotToggle(checked, selectedAutopilotType);
      setAutopilotEnabledState(!!status.enabled);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to toggle Autopilot');
    } finally {
      setAutopilotBusy(false);
    }
  };

  const openTradesPopup = () => {
    // Every open is a full reload — never show whatever was left over from
    // a previous open while the fresh fetch is still in flight.
    setPositions([]);
    setPositionsError('');
    setShowTradesPopup(true);
    fetchPositions();
  };

  const handleSort = (key) => {
    let dir = 'desc';
    if (sortKey === key) dir = sortDir === 'desc' ? 'asc' : 'desc';
    setSortKey(key);
    setSortDir(dir);
    setRows(prev => sortRows(prev, key, dir));
  };

  const toggleExpand = (symbol) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(symbol)) {
        next.delete(symbol);
      } else {
        next.add(symbol);
        fetchHistoryFor(symbol);
      }
      return next;
    });
  };

  const handleClose = async (symbol) => {
    setClosingSymbol(symbol);
    try {
      await squareOffAnalysisSymbol(symbol);
      setMessage(`${symbol} squared off`);
      fetchRows();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Square-off failed');
    } finally {
      setClosingSymbol(null);
    }
  };

  const handleManualDownload = async () => {
    try {
      const response = await downloadAnalysisHistory();
      saveBlobResponse(response, `analysis-history-${istDateStr()}.csv`);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Download failed');
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm("Clear all of today's grid data and chevron history? This can't be undone.")) return;
    setCleaningUp(true);
    try {
      await cleanupAnalysisData();
      setExpanded(new Set());
      setHistoryBySymbol({});
      setMessage('Grid data cleaned up');
      fetchRows();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Clean-up failed');
    } finally {
      setCleaningUp(false);
    }
  };

  // Both the main grid's and the Trades popup's errors surface through one
  // shared alert popup instead of an inline banner — whichever is set.
  const alertMessage = error || positionsError;
  const dismissAlert = () => { setError(''); setPositionsError(''); };

  const handleAutopilotSwitchClick = () => {
    if (autopilotEnabled) {
      handleAutopilotToggle(false);
      return;
    }
    // Refresh the eligible-algo list right before showing the confirm
    // popup — it may have changed on the Algos page since this page loaded.
    getAutopilotStatus()
      .then(status => {
        setEligibleAlgos(status.eligibleAlgos || []);
        setSelectedAutopilotType(prev => (status.eligibleAlgos || []).some(a => a.type === prev) ? prev : (status.eligibleAlgos?.[0]?.type || ''));
        setShowAutopilotConfirm(true);
      })
      .catch(() => setShowAutopilotConfirm(true));
  };

  // On → open the config popup pre-filled with the last saved values; it is
  // only actually switched on by the popup's Enable button. Off → straight off.
  const handleManagePnLSwitchClick = async () => {
    if (managePnLEnabled) {
      setManagePnLBusy(true);
      try {
        const status = await setManagePnL(false);
        setManagePnLStatus(status);
        setManagePnLEnabled(!!status.enabled);
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to switch off Manage My P/L');
      } finally {
        setManagePnLBusy(false);
      }
      return;
    }
    let saved = {};
    try { saved = (await getManagePnL()).settings || {}; } catch {}
    setManagePnLForm({
      max_loss: saved.max_loss || '',
      profit_activation_threshold: saved.profit_activation_threshold || '',
      min_profit_ratio: saved.min_profit_ratio || 75,
      full_square_off: !!saved.full_square_off,
    });
  };

  // Test Mode: with no open positions in scope it just flips; otherwise the
  // resize popup (increase when switching off, decrease when switching on)
  // decides what happens to them first.
  const applyTestMode = async (enabling, choice) => {
    setTestModeBusy(true);
    try {
      setTestModeState(await setTestMode(enabling, choice, product));
      setTestModePopup(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to switch Test Mode');
    } finally {
      setTestModeBusy(false);
    }
  };

  const handleTestModeSwitchClick = async () => {
    const enabling = !testMode.enabled;
    setTestModeBusy(true);
    let count = 0;
    try { count = (await getTestModeScope(product)).count; }
    catch (err) { setError(err.response?.data?.error || err.message || 'Failed to check open positions'); setTestModeBusy(false); return; }
    setTestModeBusy(false);
    if (!count) { applyTestMode(enabling, null); return; }
    setTestModePopup({ enabling, choice: enabling ? 0 : 1 });
  };

  const handleManagePnLEnable = async () => {
    setManagePnLBusy(true);
    try {
      const status = await setManagePnL(true, managePnLForm);
      setManagePnLStatus(status);
      setManagePnLEnabled(!!status.enabled);
      setManagePnLForm(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to enable Manage My P/L');
    } finally {
      setManagePnLBusy(false);
    }
  };

  return (
    <div className="analysis-page">
      {loading && <div className="an-header"><span className="an-loading">Loading…</span></div>}
      {testMode.enabled && (
        <div className="an-banner an-banner-info">
          Test Mode is on — orders are sized at 10% of the trade value (minimum 1 share), Max Position Quantity ×10.
        </div>
      )}
      {testMode.adjustment && (!testMode.adjustment.done || Date.now() - Date.parse(testMode.adjustment.startedAt) < 10 * 60 * 1000) && (() => {
        const { kind, factor, done, orders } = testMode.adjustment;
        const filled = orders.filter(o => o.filled > 0).length;
        const label = kind === 'increase' ? `Increase by ${factor}×` : `Decrease by ${factor}%`;
        return (
          <div className="an-banner an-banner-info">
            {label}: {done ? 'finished' : 'in progress'} — {filled}/{orders.length} order(s) filled
            {orders.length > 0 && ': '}
            {orders.map(o => `${o.symbol}${o.owner !== 'Manual' ? ` (${o.owner})` : ''} ${o.side} ${o.filled}/${o.qty}${o.status === 'FAILED' ? ' failed' : o.status === 'CANCELLED' ? ' cancelled' : o.status === 'REJECTED' ? ' rejected' : ''}`).join(', ')}
          </div>
        );
      })()}
      {managePnLEnabled && managePnLStatus && (() => {
        const { floor, settings = {} } = managePnLStatus;
        const fmtR = v => `₹${Math.round(v).toLocaleString('en-IN')}`;
        const minProfit = floor != null
          ? `current Min Profit: ${fmtR(floor)}`
          : settings.profit_activation_threshold > 0
            ? `Min Profit not active yet — activates once P&L reaches ${fmtR(settings.profit_activation_threshold)}`
            : 'Min Profit not set';
        return (
          <div className="an-banner an-banner-info">
            Manage My P/L is on — {minProfit}{settings.max_loss > 0 ? ` · Max Loss: ${fmtR(settings.max_loss)}` : ''}
          </div>
        );
      })()}
      {message && <div className="an-banner an-banner-ok">{message}</div>}
      <div className="an-config">
        <div className="an-config-fields">
          <div className="an-config-field">
            <label>Autopilot</label>
            <button
              className={`an-autopilot-switch ${autopilotEnabled ? 'an-autopilot-switch-on' : ''}`}
              onClick={handleAutopilotSwitchClick}
              disabled={autopilotBusy}
              title={autopilotEnabled ? 'Switch Autopilot off' : 'Switch Autopilot on'}
            >
              <span className="an-autopilot-switch-knob" />
            </button>
          </div>
          <div className="an-config-field">
            <label>Test Mode</label>
            <button
              className={`an-autopilot-switch ${testMode.enabled ? 'an-autopilot-switch-on' : ''}`}
              onClick={handleTestModeSwitchClick}
              disabled={testModeBusy || !!testModePopup}
              title={testMode.enabled ? 'Switch Test Mode off — trade at the full trade value' : 'Switch Test Mode on — trade at 10% of the trade value'}
            >
              <span className="an-autopilot-switch-knob" />
            </button>
          </div>
          <div className="an-config-field">
            <label>Manage My P/L</label>
            <button
              className={`an-autopilot-switch ${managePnLEnabled ? 'an-autopilot-switch-on' : ''}`}
              onClick={handleManagePnLSwitchClick}
              disabled={managePnLBusy}
              title={managePnLEnabled ? 'Switch Manage My P/L off' : 'Switch Manage My P/L on — manages the P&L of every MIS position, manual or algo'}
            >
              <span className="an-autopilot-switch-knob" />
            </button>
          </div>
          <div className="an-config-field">
            <label>Traded Value</label>
            <input type="number" min="0" step="1" value={amount} onChange={e => setAmount(e.target.value)} title="0 = no orders from this page (Buy/Sell and the Max Position Quantity sweep are both off)" />
          </div>
          <div className="an-config-field">
            <label>Product Type</label>
            <select className="an-product-select" value={product} onChange={e => setProduct(e.target.value)}>
              <option value="MIS">MIS (Intraday)</option>
              <option value="CNC">CNC (Delivery)</option>
            </select>
          </div>
          <div className="an-config-field">
            <label>Max Position Quantity</label>
            <select value={maxQtyMultiplier} onChange={e => handleMaxQtyMultiplierChange(parseInt(e.target.value, 10))}>
              {Array.from({ length: 100 }, (_, i) => i + 1).map(n => (
                <option key={n} value={n}>{n}x</option>
              ))}
            </select>
          </div>
        </div>
        <button className="an-trades-btn" title="Trades" onClick={openTradesPopup}>🔔</button>
      </div>
      <div className="an-suggestions-row">
        <SuggestionCard slot="left" suggestion={suggestions[0] || null} />
        <SuggestionCard slot="right" suggestion={suggestions[1] || null} />
      </div>
      <div className="an-table-wrap">
        <table className="an-table">
          <thead>
            <tr>
              <th className="an-chevron-col" />
              {COLUMNS.map(col => (
                <th key={col.key} onClick={() => handleSort(col.key)} className={`an-sortable ${col.key === 'symbol' ? 'an-symbol-header' : ''}`}>
                  {col.label}
                  {sortKey === col.key && <span className="an-sort-arrow">{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>}
                </th>
              ))}
              <th>Positions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading ? (
              <tr><td colSpan={COLUMNS.length + 2} className="an-no-data">No data — market may be closed</td></tr>
            ) : rows.map(row => {
              const hasPosition = !!row.position;
              const isExpanded = expanded.has(row.symbol);
              const history = historyBySymbol[row.symbol] || [];
              const rowClass = [hasPosition ? 'an-row-position' : '', row.isNew ? 'an-row-new' : ''].filter(Boolean).join(' ');
              const suggestedDir = computeSuggestedDirection(row);
              const isPlacingThisRow = placingOrder?.symbol === row.symbol;
              const currentQty = row.position?.quantity || 0;
              const buyCapped = isCapBlocked('BUY', currentQty, row.qty, row.maxQty, product);
              const sellCapped = isCapBlocked('SELL', currentQty, row.qty, row.maxQty, product);
              return (
                <React.Fragment key={row.symbol}>
                  <tr className={rowClass}>
                    <td className="an-chevron-col">
                      <button className="an-chevron-btn" onClick={() => toggleExpand(row.symbol)} title="Today's history">
                        {isExpanded ? '▼' : '▶'}
                      </button>
                    </td>
                    <td className="an-symbol">{row.symbol}</td>
                    <DataCells data={row} />
                    <td>
                      <div className="an-actions">
                        <button
                          className={`an-btn-trade ${suggestedDir === 'BUY' ? 'an-btn-trade-recommended-buy' : ''}`}
                          onClick={() => handleTrade(row, 'BUY')}
                          disabled={isPlacingThisRow || buyCapped}
                          title={buyCapped ? 'Would exceed Max Position Quantity' : undefined}
                        >
                          {isPlacingThisRow && placingOrder.type === 'BUY' ? '…' : 'Buy'}
                        </button>
                        <button
                          className={`an-btn-trade ${suggestedDir === 'SELL' ? 'an-btn-trade-recommended-sell' : ''}`}
                          onClick={() => handleTrade(row, 'SELL')}
                          disabled={isPlacingThisRow || sellCapped}
                          title={sellCapped ? 'Would exceed Max Position Quantity' : undefined}
                        >
                          {isPlacingThisRow && placingOrder.type === 'SELL' ? '…' : 'Sell'}
                        </button>
                        {hasPosition && (
                          <button className="an-btn-close" onClick={() => handleClose(row.symbol)} disabled={closingSymbol === row.symbol}>
                            {closingSymbol === row.symbol ? '…' : 'Close'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {isExpanded && history.map((snap, idx) => (
                    <tr key={`${row.symbol}-${idx}`} className="an-subrow">
                      <td className="an-chevron-col" />
                      <td className="an-symbol an-subrow-time">{formatISTTime(snap.ts)}</td>
                      <DataCells data={snap} />
                      <td />
                    </tr>
                  ))}
                  {isExpanded && history.length === 0 && (
                    <tr className="an-subrow">
                      <td className="an-chevron-col" />
                      <td colSpan={COLUMNS.length} className="an-no-data">No history yet today</td>
                      <td />
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="an-footer">
        <button className="an-btn-cleanup" onClick={handleCleanup} disabled={cleaningUp}>
          {cleaningUp ? 'Cleaning…' : '🧹 Clean-up'}
        </button>
        <button className="an-btn-download" onClick={handleManualDownload}>⬇ Download Today's Data</button>
      </div>

      {showTradesPopup && (
        <TradesPopup
          positions={positions}
          loading={positionsLoading}
          tradingSymbol={tradingSymbol}
          onClose={() => setShowTradesPopup(false)}
          onTrade={handlePopupTrade}
        />
      )}

      {alertMessage && (
        <div className="an-modal-backdrop" onClick={dismissAlert}>
          <div className="an-modal an-alert-modal" onClick={e => e.stopPropagation()}>
            <div className="an-trades-modal-header">
              <div className="an-modal-title">Error</div>
              <button className="an-trades-modal-close" onClick={dismissAlert} title="Close">×</button>
            </div>
            <p className="an-alert-text">{alertMessage}</p>
            <div className="an-modal-actions">
              {alertMessage === AUTOPILOT_BLOCK_MESSAGE && (
                <button className="an-modal-cancel" onClick={() => { dismissAlert(); handleAutopilotToggle(false); }}>
                  Turn off Autopilot
                </button>
              )}
              <button className="an-modal-submit" onClick={dismissAlert}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showAutopilotConfirm && (
        <div className="an-modal-backdrop" onClick={() => setShowAutopilotConfirm(false)}>
          <div className="an-modal an-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="an-modal-title">Turn on Autopilot?</div>
            <p className="an-confirm-text">
              The max quantity will be reduced, which may result in square off on extra positions. Are you ready to switch autopilot on?
            </p>
            <div className="an-modal-row">
              <label>Select an algorithm</label>
              {eligibleAlgos.length === 0 ? (
                <p className="an-confirm-text">
                  No algorithm is enabled for Autopilot yet — go to the Algos page's Complex Strategies tab and enable one (the ⚡ button on its row).
                </p>
              ) : (
                <select value={selectedAutopilotType} onChange={e => setSelectedAutopilotType(e.target.value)}>
                  {eligibleAlgos.map(a => (
                    <option key={a.type} value={a.type}>{a.name}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="an-modal-actions">
              <button className="an-modal-cancel" onClick={() => setShowAutopilotConfirm(false)}>No</button>
              <button
                className="an-modal-confirm-yes"
                onClick={() => { setShowAutopilotConfirm(false); handleAutopilotToggle(true); }}
                disabled={!selectedAutopilotType}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {testModePopup && (
        // No click-outside-to-close — only Cancel / Apply dismiss it.
        <div className="an-modal-backdrop">
          <div className="an-modal an-confirm-modal">
            <div className="an-modal-title">{testModePopup.enabling ? 'Switch Test Mode on' : 'Switch Test Mode off'}</div>
            <p className="an-confirm-text">
              {testModePopup.enabling
                ? 'From now on orders use 10% of the trade value. Reduce your open positions now with MARKET reverse orders?'
                : 'From now on orders use the full trade value. Grow your open positions now with LIMIT orders at the current price (unfilled after 2 minutes → cancelled)?'}
              {' '}Applies to open {autopilotEnabled ? 'MIS' : product} positions.
            </p>
            <div className="an-modal-row">
              <label>{testModePopup.enabling ? 'Decrease position quantity by' : 'Increase position quantity by'}</label>
              <select value={testModePopup.choice} onChange={e => setTestModePopup(p => ({ ...p, choice: Number(e.target.value) }))}>
                {(testModePopup.enabling ? [0, 10, 20, 30, 40, 50, 60, 70, 80, 90] : [1, 2, 3, 4, 5, 6, 7, 8, 9]).map(v => (
                  <option key={v} value={v}>{testModePopup.enabling ? `${v}%` : `${v}x`}</option>
                ))}
              </select>
              <p className="an-confirm-text">
                {testModePopup.enabling
                  ? (testModePopup.choice === 0 ? 'Positions stay as they are.' : `Each position is reduced by ${testModePopup.choice}% of its current quantity.`)
                  : (testModePopup.choice === 1 ? 'Positions stay as they are.' : `An order for ${testModePopup.choice} × current quantity is added to each position (${testModePopup.choice + 1}× in total).`)}
              </p>
            </div>
            <div className="an-modal-actions">
              <button className="an-modal-cancel" onClick={() => setTestModePopup(null)} disabled={testModeBusy}>Cancel</button>
              <button className="an-modal-confirm-yes" onClick={() => applyTestMode(testModePopup.enabling, testModePopup.choice)} disabled={testModeBusy}>
                {testModePopup.enabling ? 'Switch on' : 'Switch off'}
              </button>
            </div>
          </div>
        </div>
      )}

      {managePnLForm && (
        // No click-outside-to-close — only Cancel / Enable dismiss it, so a
        // stray click can't throw away what was typed.
        <div className="an-modal-backdrop">
          <div className="an-modal an-confirm-modal">
            <div className="an-modal-title">Manage My P/L</div>
            <p className="an-confirm-text">
              Manages the P&amp;L (after charges) of all your MIS positions — manual and algo — every 10 seconds. Enabling clears the Algos page's global Max Profit / Max Loss / Min Profit settings.
            </p>
            <div className="an-modal-row">
              <label>Max Loss (₹)</label>
              <input type="number" min="0" step="100" value={managePnLForm.max_loss}
                onChange={e => setManagePnLForm(f => ({ ...f, max_loss: e.target.value }))} />
            </div>
            <div className="an-modal-row">
              <label>Profit Value to Activate Min Profit (₹)</label>
              <input type="number" min="0" step="100" value={managePnLForm.profit_activation_threshold}
                onChange={e => setManagePnLForm(f => ({ ...f, profit_activation_threshold: e.target.value }))} />
            </div>
            <div className="an-modal-row">
              <label>Min Profit Value vs Current Profit (%)</label>
              <input type="number" min="1" max="100" step="5" value={managePnLForm.min_profit_ratio}
                onChange={e => setManagePnLForm(f => ({ ...f, min_profit_ratio: e.target.value }))} />
            </div>
            <div className="an-modal-row">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={managePnLForm.full_square_off} style={{ width: 'auto' }}
                  onChange={e => setManagePnLForm(f => ({ ...f, full_square_off: e.target.checked }))} />
                Full square off
              </label>
              <p className="an-confirm-text">
                {managePnLForm.full_square_off
                  ? 'On Max Loss or Min Profit, every open MIS position is squared off.'
                  : 'On Max Loss or Min Profit, only positions in loss — or held against this page\'s Buy/Sell suggestion — are squared off.'}
              </p>
            </div>
            <div className="an-modal-actions">
              <button className="an-modal-cancel" onClick={() => setManagePnLForm(null)}>Cancel</button>
              <button
                className="an-modal-confirm-yes"
                onClick={handleManagePnLEnable}
                disabled={managePnLBusy || !(parseFloat(managePnLForm.max_loss) > 0 || parseFloat(managePnLForm.profit_activation_threshold) > 0)}
              >
                Enable
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnalysisPage;
